import { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from '../context';
import profileService from '../services/profileService';
import { handleCandidates } from '../components/introduction/introductionModel';
import { decodePortalPage, buildPortalPayload, decodeExtended } from '../utils/portalPageModel';

/**
 * The Portal Page being edited: one in-memory page object (utils/portalPageModel), dirty =
 * deep-compare against the last saved snapshot. `/portal/edit` and the introduction's edit sheet
 * both run on this, so there is one save.
 *
 * Save is one PUT of the whole page (the intake's payload + the extendedProfile keys), then the
 * gallery calls the PUT cannot express — removed photos, their order, and the profile photo cropped
 * from its gallery photo at the chosen focal point — then a fresh read. Uploads happen the moment a
 * file is picked (the file has to live somewhere) and join the saved snapshot, so an upload alone
 * is never "unsaved"; a removal waits for SAVE so DISCARD can bring the photo back. `save` resolves
 * to whether the page on the server now matches.
 *
 * Legacy accounts (an avatar uploaded on its own, before the editor) are adopted on open:
 * POST gallery/adopt-avatar makes the avatar photo 1 of the gallery so it is one object to edit.
 */
export default function usePortalEditor({ onHandleBlocked } = {}) {
  const { profile, fetchProfile } = useUser();
  const [dto, setDto] = useState(null);          // the server's copy, for the payload + the lists
  const [page, setPage] = useState(null);        // what is being edited
  const [saved, setSaved] = useState('');        // JSON of the last saved page
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [handleState, setHandleState] = useState({ checking: false, available: null, message: null, changed: false });
  const savedTimer = useRef(null);

  const adopt = (p) => { setDto(p); const pg = decodePortalPage(p); setPage(pg); setSaved(JSON.stringify(pg)); };

  // Hydrate once the context has the profile; migrate a legacy avatar into the gallery first.
  useEffect(() => {
    if (!profile || dto) return undefined;
    let cancelled = false;
    (async () => {
      let p = profile;
      if (decodePortalPage(p).photos.some((x) => x.legacy)) {
        const r = await profileService.adoptAvatarIntoGallery();
        if (r.success && r.data) p = r.data;
      }
      if (!cancelled) adopt(p);
    })();
    return () => { cancelled = true; };
  }, [profile, dto]);

  useEffect(() => () => clearTimeout(savedTimer.current), []);

  const dirty = !!page && JSON.stringify(page) !== saved;

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  // Sign-up never asks for a handle, and a page cannot save without one. With none saved, offer the
  // free ones the name makes as pills and fill in the best; the box stays editable.
  const [handleOptions, setHandleOptions] = useState([]);
  const nameForHandle = dto && !dto.handle ? (page?.name || '') : null;
  useEffect(() => {
    if (nameForHandle == null) { setHandleOptions([]); return undefined; }
    let on = true;
    const t = setTimeout(async () => {
      const candidates = handleCandidates(nameForHandle);
      const checks = await Promise.all(candidates.map((c) => profileService.checkHandle(c)));
      if (!on) return;
      const free = candidates.filter((_, i) => checks[i].success && checks[i].data?.available).slice(0, 4);
      setHandleOptions(free);
      if (free[0]) setPage((p) => (p && !p.handle ? { ...p, handle: free[0] } : p));
    }, 300);
    return () => { on = false; clearTimeout(t); };
  }, [nameForHandle]);

  // Handle availability, debounced, only when it differs from the saved one.
  useEffect(() => {
    if (!page) return undefined;
    const h = page.handle || '';
    if (h === (dto?.handle || '')) { setHandleState({ checking: false, available: true, message: null, changed: false }); return undefined; }
    if (h.length < 3) { setHandleState({ checking: false, available: null, message: null, changed: true }); return undefined; }
    setHandleState({ checking: true, available: null, message: null, changed: true });
    const t = setTimeout(async () => {
      const r = await profileService.checkHandle(h);
      if (r.success) setHandleState({ checking: false, available: r.data.available, message: r.data.message, changed: true });
      else setHandleState({ checking: false, available: null, message: 'Could not check availability', changed: true });
    }, 500);
    return () => clearTimeout(t);
  }, [page?.handle, dto?.handle]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = useCallback((patch) => {
    setPage((s) => ({ ...s, ...(typeof patch === 'function' ? patch(s) : patch) }));
    setSaveState('idle');
  }, []);

  const onUpload = async (file) => {
    setUploading(true);
    const r = await profileService.uploadGalleryPhoto(file);
    setUploading(false);
    if (!r.success || !r.data) return r.error || 'Could not upload that photo.';
    const photo = { id: String(r.data.id), src: r.data.url, fx: 50, fy: 50 };
    // The first photo of an account with no profile photo becomes it — in the page only, so the
    // crop is still owed to SAVE (the snapshot keeps avatar: null and the page reads as dirty).
    setPage((s) => ({ ...s, photos: [...s.photos, photo], avatar: s.avatar || photo.id }));
    setSaved((prev) => { const b = JSON.parse(prev); b.photos.push(photo); return JSON.stringify(b); });
    setDto((d) => ({ ...d, galleryPhotos: [...(d.galleryPhotos || []), r.data] }));
    return null;
  };

  const handleBlocked = page ? ((page.handle || '').length < 3 || handleState.available === false || handleState.checking) : false;
  const canSave = dirty && saveState !== 'saving' && !handleBlocked;

  const save = async () => {
    if (!canSave) { if (handleBlocked) onHandleBlocked?.(); return !dirty && !handleBlocked; }
    setSaveState('saving');
    setError(null);
    const base = JSON.parse(saved);
    const fail = (msg) => { setError(msg || 'Could not save your changes. Try again.'); setSaveState('idle'); };

    const put = await profileService.updateProfile(buildPortalPayload(page, dto));
    if (!put.success) { fail(put.error); return false; }
    let latest = put.data || dto;

    const removed = base.photos.filter((p) => !p.legacy && !page.photos.some((q) => q.id === p.id));
    for (const p of removed) {
      const r = await profileService.deleteGalleryPhoto(p.id); // eslint-disable-line no-await-in-loop
      if (!r.success) { fail(r.error || 'A photo could not be removed.'); return false; }
    }

    const ids = page.photos.filter((p) => !p.legacy).map((p) => p.id);
    const serverIds = (latest.galleryPhotos || []).map((p) => String(p.id)).filter((id) => ids.includes(id));
    if (ids.length && ids.join('|') !== serverIds.join('|')) {
      const r = await profileService.reorderGalleryPhotos(ids);
      if (!r.success) { fail(r.error || 'The photo order could not be saved.'); return false; }
      latest = r.data || latest;
    }

    // The profile photo is owed a crop when the server's avatar is not this photo at this focal
    // point — compared against what the API actually holds, not the snapshot, so a first photo on an
    // account with no avatar yet gets cropped too. With no saved focus (an adopted legacy avatar) only
    // a drag counts, so an unrelated save never silently re-crops an old avatar.
    const dext = decodeExtended(dto?.extendedProfile);
    const serverAvatar = dext.avatarPhotoId ? String(dext.avatarPhotoId) : null;
    const sf = dext.avatarFocus && typeof dext.avatarFocus === 'object' ? dext.avatarFocus : null;
    const av = page.photos.find((p) => p.id === page.avatar);
    const avatarChanged = !dto?.avatarUrl || page.avatar !== serverAvatar || (sf ? (page.ax !== sf.x || page.ay !== sf.y) : (page.ax !== base.ax || page.ay !== base.ay));
    if (av && !av.legacy && avatarChanged) {
      const r = await profileService.setAvatarFromGallery(av.id, page.ax, page.ay);
      if (!r.success) { fail(r.error || 'The profile photo could not be saved.'); return false; }
      latest = r.data || latest;
    }

    const fresh = await profileService.getProfile();
    if (fresh.success && fresh.data) latest = fresh.data;
    adopt(latest);
    fetchProfile();
    setSaveState('saved');
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaveState('idle'), 1600);
    return true;
  };

  const discard = () => { setPage(JSON.parse(saved)); setSaveState('idle'); setError(null); };

  return { dto, page, upd, dirty, canSave, save, discard, saveState, error, uploading, onUpload, handleState, handleOptions };
}
